import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Maximize2 } from "lucide-react";
import { AvatarRing } from "../../../common/comms/AvatarRing";
import { EmptyState } from "../../../common/comms/EmptyState";
import { StatusBadge } from "../../../common/comms/StatusBadge";
import { useClientProfile } from "../../../common/comms/ClientProfileProvider";
import { parseLocalDate } from "../../../utils/formatLocalDate";
import { canOpenStatusEditor } from "./bookingAuthority";
import { normaliseStatus, STATUS, statusTone } from "./bookingStatus";
import {
  feedbackOf,
  hasClientFeedback,
  ratingLabel,
  ratingOf,
  summariseClientActivity,
  trailByDay,
  trailOnDay,
} from "./clientActivity";
import { formatShortDate, formatSlotRange } from "./slotGrid";

/** A row's key: its booking id, or its position when the record has none. */
const rowKeyOf = (record, index) => String(record?.id ?? `row-${index}`);

/** Stable avatar colour per client — the same seed the agenda rows use. */
const colorIndexFor = (id) => (id ? String(id).charCodeAt(0) % 8 : 0);

/** "12 Sept, 10:02" for a status change. */
const formatChangedAt = (date) =>
  date
    ? date.toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        hour: "numeric",
        minute: "2-digit",
      })
    : "";

/** The 1–5 rating as one decimal, the way the app shows it: 4 → "4.0". */
const formatRating = (rating) => rating.toFixed(1);

/**
 * What the client said about the call: the rating as a word and a five-step
 * bar, and their written feedback underneath. Either may be missing — a client
 * can rate without writing anything.
 *
 * Read-only. The client writes it from the app once the call has ended; there
 * is no rated-at time to show, because the API does not record one.
 */
const ClientFeedback = ({ id, rating, feedback }) => (
  <div id={id} className="mt-2.5 border-t border-bc-hairline pt-2.5">
    {rating != null ? (
      <>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-semibold text-bc-text">{ratingLabel(rating)}</span>
          <span className="text-xs font-semibold text-bc-soft">{`${formatRating(rating)} / 5`}</span>
        </div>
        <div
          role="img"
          aria-label={`Rated ${Number(formatRating(rating))} out of 5`}
          className="mt-1.5 flex gap-1"
        >
          {[1, 2, 3, 4, 5].map((step) => (
            <span
              key={step}
              className={`h-1.5 flex-1 rounded-full ${
                step <= Math.round(rating) ? "bg-orange-primary" : "bg-gray-200"
              }`}
            />
          ))}
        </div>
      </>
    ) : null}
    {feedback ? (
      <blockquote className={`flex gap-1.5 ${rating != null ? "mt-2.5" : ""}`}>
        <span aria-hidden="true" className="text-base font-semibold leading-none text-orange-primary">
          “
        </span>
        <p className="whitespace-pre-line text-xs italic text-bc-text-2">{feedback}</p>
      </blockquote>
    ) : null}
  </div>
);

/**
 * One call: when it was, what it is now, every status it went through, and —
 * once the client has rated it — what they thought of it.
 *
 * Still not a navigation target — every row here is the same client and the
 * same coach, so there is nowhere further to *go*, and opening the full profile
 * remains a header action. What a row can carry is an action on the call it
 * describes, which is why Set Status appears on it.
 *
 * The history list is shown only once there is more than the opening
 * "Confirmed" — a single entry would repeat the badge above it.
 *
 * The rating sits beside the status as "★ 4.0", and the feedback folds behind
 * View feedback — **only while the call's latest status is Completed**
 * (`hasClientFeedback`). A call rated and then corrected to No Show keeps its
 * stored rating, but shows none of it. A Completed call with neither says
 * "Not rated" — the one status where its absence means something. Other
 * statuses say nothing.
 */
const ActivityRow = ({ record, rowKey, onSetStatus, canSetStatus, expanded, onToggleFeedback }) => {
  const showFeedback = hasClientFeedback(record);
  const rating = showFeedback ? ratingOf(record) : null;
  const feedback = showFeedback ? feedbackOf(record) : "";
  const notRated =
    !showFeedback && normaliseStatus(record.booking_status) === STATUS.COMPLETED;
  const feedbackId = `call-feedback-${rowKey}`;

  return (
    // `relative` keeps the `.sr-only` label below inside the card — see BookingsTab.
    <li data-row-key={rowKey} className="relative overflow-hidden rounded-lg border border-bc-hairline">
      <div className="px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm font-semibold text-bc-text">
            {formatShortDate(record.startsAt)}
          </span>
          <span className="text-sm font-semibold text-orange-primary">
            {formatSlotRange(record)}
          </span>
          <span className="ml-auto flex items-center gap-1.5">
            {rating != null ? (
              <StatusBadge tone="brand">
                <span className="sr-only">Rated</span>
                <span aria-hidden="true">★</span>
                {formatRating(rating)}
              </StatusBadge>
            ) : notRated ? (
              <span className="inline-flex items-center rounded-full border border-dashed border-gray-300 px-2 py-0.5 text-[11px] font-semibold text-bc-faint">
                Not rated
              </span>
            ) : null}
            <StatusBadge tone={statusTone(record.booking_status)}>
              {record.booking_status || "Unknown"}
            </StatusBadge>
          </span>
        </div>
        {Array.isArray(record.history) && record.history.length > 1 ? (
          <ol className="mt-2 space-y-1 border-l border-bc-hairline pl-3" aria-label="Status history">
            {record.history.map((entry) => (
              <li key={entry.id} className="text-xs text-bc-soft">
                <span className="font-semibold text-bc-text-2">{entry.status}</span>
                {entry.changedAt ? ` · ${formatChangedAt(entry.changedAt)}` : ""}
                {entry.reason ? <span className="block">{entry.reason}</span> : null}
              </li>
            ))}
          </ol>
        ) : record.status_reason ? (
          <p className="mt-1 text-xs text-bc-soft">{record.status_reason}</p>
        ) : null}
        {showFeedback && expanded ? (
          <ClientFeedback id={feedbackId} rating={rating} feedback={feedback} />
        ) : null}
        {canSetStatus ? (
          <button
            type="button"
            onClick={() => onSetStatus(record)}
            aria-label={`Set status for the call on ${formatShortDate(
              record.startsAt
            )}, ${formatSlotRange(record)}`}
            className="mt-2 min-h-[32px] rounded-lg border border-gray-200 px-2.5 text-xs font-semibold text-bc-text-2 transition-colors hover:bg-light-gray/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-1"
          >
            Set status
          </button>
        ) : null}
      </div>
      {showFeedback ? (
        <button
          type="button"
          onClick={() => onToggleFeedback(rowKey)}
          aria-expanded={expanded}
          aria-controls={expanded ? feedbackId : undefined}
          className="flex min-h-[36px] w-full items-center justify-between border-t border-bc-hairline px-3 text-xs font-semibold text-orange-primary transition-colors hover:bg-light-orange/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-orange-primary"
        >
          {expanded ? "Hide feedback" : "View feedback"}
          {expanded ? (
            <ChevronUp className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      ) : null}
    </li>
  );
};

/**
 * A client's booking activity — the contents of the workspace's side drawer.
 *
 * One row per call, each with its status history underneath: a call that was
 * booked and then cancelled reads "Cancelled", with Confirmed → Cancelled and
 * the reason for each change. The agenda behind it shows each client once, and
 * this is where the calls it folded away — and the status changes the agenda
 * never showed at all — become visible.
 *
 * **Opens on the day it was clicked from** (`dayKey`): that day's calls only,
 * latest first, so the top card is the call the agenda row stood for — not a
 * later day's booking elsewhere in the month. "Show all in <month>" widens it
 * to every call that month, grouped by day with the clicked day first; that
 * stays here because this drawer is the only place another day's calls can be
 * reached without going back to the calendar. The header counts whatever is
 * showing, so its numbers never disagree with the list.
 *
 * Never wider than the month the calendar is on, and it says so. There is no
 * per-client history endpoint the frontend may call (`fetch-booking-records`
 * 403s on somebody else's `user_id`), so this reads the month already fetched.
 *
 * @param {Object} props
 * @param {string} [props.clientId] - UserProfile id. Without it the profile
 *   link is withheld rather than opening an empty drawer.
 * @param {string} [props.clientName]
 * @param {string} [props.dayKey] - `YYYY-MM-DD` the drawer was opened from.
 *   Without it the whole month shows, with no day to go back to.
 * @param {boolean} [props.initialShowAll=false] - Start on the month view (the
 *   profile's Back reopening the drawer as it was left).
 * @param {Array<Object>} props.trail - From `clientTrailForEmployee`, newest first.
 * @param {string} props.monthLabel - e.g. "September 2026".
 * @param {() => void} props.onClose - Closes the drawer this sits in.
 * @param {({ id: string, name: string, dayKey?: string, showAll: boolean }) => void} [props.onReopen] - Puts this
 *   drawer back up on the same client, day and view. Passed to the profile as its Back
 *   action, so opening the profile is a step forward rather than a dead end —
 *   without it the profile's only exit is ✕, which drops you on the agenda.
 * @param {(booking: Object) => void} [props.onSetStatus] - Opens the status
 *   editor for one call. This panel is the **only** way to reach a client's
 *   earlier calls on a day: the agenda behind it shows one row per client, so
 *   its own Set Status button can only ever act on their latest call.
 * @param {{ isExecutive: boolean, ownsSlot: boolean, managesSlot: boolean }} [props.statusAuthority]
 * @param {Map<string, { id: string, title: string }>} [props.statusIndex]
 * @param {string} [props.focusBookingId] - The call the drawer was opened for
 *   (the agenda's rating chip): its feedback starts unfolded and it is
 *   scrolled into view.
 */
export const ClientActivityPanel = ({
  clientId,
  clientName,
  dayKey,
  initialShowAll = false,
  trail,
  monthLabel,
  onClose,
  onReopen,
  onSetStatus,
  statusAuthority,
  statusIndex,
  focusBookingId,
}) => {
  const { openClientProfile } = useClientProfile();
  const [showAll, setShowAll] = useState(Boolean(initialShowAll || !dayKey));
  // Which calls have their feedback open, by row key. A call the drawer was
  // opened *for* (the agenda's rating chip) starts open; otherwise the first
  // call it opens on that has any does, so the latest word from the client is
  // visible without a click. The rest stay folded. The panel is remounted per
  // client, day and focused call, so this never carries over.
  const [expandedKeys, setExpandedKeys] = useState(() => {
    const focused =
      focusBookingId != null
        ? trail.find((record) => String(record?.id) === String(focusBookingId))
        : null;
    if (focused && hasClientFeedback(focused)) return new Set([rowKeyOf(focused)]);
    const opening = initialShowAll || !dayKey ? trail : trailOnDay(trail, dayKey);
    const index = (Array.isArray(opening) ? opening : []).findIndex(hasClientFeedback);
    return new Set(index < 0 ? [] : [rowKeyOf(opening[index], index)]);
  });
  // Brings the focused call into view: it is usually the top card already (the
  // agenda row stands for the day's latest call), but not on a long day.
  const listRef = useRef(null);
  useEffect(() => {
    if (focusBookingId == null) return;
    const card = listRef.current?.querySelector(
      `[data-row-key="${String(focusBookingId).replace(/"/g, '\\"')}"]`
    );
    card?.scrollIntoView?.({ block: "nearest" });
  }, [focusBookingId]);
  const toggleFeedback = (key) =>
    setExpandedKeys((keys) => {
      const next = new Set(keys);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const dayLabel = dayKey ? formatShortDate(parseLocalDate(dayKey)) : "";
  const visible = useMemo(
    () => (showAll ? trail : trailOnDay(trail, dayKey)),
    [showAll, trail, dayKey]
  );
  const groups = useMemo(
    () => (showAll ? trailByDay(trail, dayKey) : []),
    [showAll, trail, dayKey]
  );
  const summary = useMemo(() => summariseClientActivity(visible), [visible]);
  // Worth offering only when the month holds calls this day doesn't.
  const hasOtherDays = trail.length > trailOnDay(trail, dayKey).length;

  const renderRow = (record, index) => (
    <ActivityRow
      key={rowKeyOf(record, index)}
      rowKey={rowKeyOf(record, index)}
      record={record}
      expanded={expandedKeys.has(rowKeyOf(record, index))}
      onToggleFeedback={toggleFeedback}
      onSetStatus={onSetStatus}
      canSetStatus={
        Boolean(onSetStatus) &&
        canOpenStatusEditor({
          authority: statusAuthority,
          booking: record,
          statusIndex,
        })
      }
    />
  );

  const linkClass =
    "ml-auto text-xs font-semibold text-orange-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary";

  const openProfile = () => {
    // Close this one first. Both drawers are `aria-modal`, and `Drawer` decides
    // an outside click with a bare `contains()` test on its own panel — leaving
    // this open behind the profile would make every click inside the profile
    // read as an outside click here. Which is also why the way back has to be
    // handed over as a callback: the activity drawer is genuinely gone by the
    // time the profile is up, so Back reopens it rather than revealing it.
    onClose();
    openClientProfile(clientId, clientName, {
      onBack: onReopen
        ? () => onReopen({ id: clientId, name: clientName, dayKey, showAll })
        : undefined,
    });
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex-shrink-0 border-b border-bc-hairline px-4 pb-4 pt-1">
        <div className="flex items-center gap-3">
          <AvatarRing
            name={clientName}
            colorIndex={colorIndexFor(clientId || clientName)}
            size="lg"
          />
          <div className="min-w-0">
            <p className="truncate text-base font-semibold text-bc-text">
              {clientName || "Unknown User"}
            </p>
            <p className="text-xs text-bc-soft">
              {/*
                Calls, not events, and cancellations left out of both — see
                `summariseClientActivity`. Counted from the list below — the
                day, or the month once "Show all" is on — so the two numbers
                can't disagree with what's on screen.
              */}
              {`${summary.calls} call${summary.calls === 1 ? "" : "s"} · ${
                summary.upcoming
              } upcoming`}
            </p>
          </div>
        </div>

        {clientId ? (
          <button
            type="button"
            onClick={openProfile}
            className="mt-3 inline-flex min-h-[40px] w-full items-center justify-center gap-2 rounded-lg border border-bc-hairline text-sm font-semibold text-bc-text-2 transition-colors hover:bg-light-gray/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-1"
          >
            <Maximize2 className="h-4 w-4" aria-hidden="true" />
            Open profile
          </button>
        ) : null}
      </div>

      <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        <div className="mb-2 flex items-baseline gap-2">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-bc-faint">
            {showAll ? "All of" : "Activity on"}
            <span className="ml-1.5 normal-case tracking-normal text-bc-soft">
              {showAll ? monthLabel : dayLabel}
            </span>
          </h3>
          {!showAll && hasOtherDays ? (
            <button type="button" onClick={() => setShowAll(true)} className={linkClass}>
              {`Show all ${trail.length} in ${monthLabel}`}
            </button>
          ) : null}
          {showAll && dayKey ? (
            <button type="button" onClick={() => setShowAll(false)} className={linkClass}>
              {`Only ${dayLabel}`}
            </button>
          ) : null}
        </div>

        {visible.length === 0 ? (
          <EmptyState
            icon={null}
            title={showAll ? "Nothing this month" : `No calls on ${dayLabel}`}
            hint={
              showAll
                ? `${clientName || "This client"} has no booking activity in ${monthLabel}.`
                : `${clientName || "This client"} has no calls on this day.`
            }
          />
        ) : showAll ? (
          <div className="space-y-4">
            {groups.map((group) => (
              <section key={group.dayKey ?? "undated"} aria-label={group.date ? formatShortDate(group.date) : "Undated"}>
                <p className="mb-1.5 text-xs font-semibold text-bc-text-2">
                  {group.date ? formatShortDate(group.date) : "Undated"}
                  {group.dayKey && group.dayKey === dayKey ? (
                    <span className="font-normal text-bc-soft"> · selected day</span>
                  ) : null}
                </p>
                <ul className="space-y-2">{group.rows.map(renderRow)}</ul>
              </section>
            ))}
          </div>
        ) : (
          <ul className="space-y-2">{visible.map(renderRow)}</ul>
        )}
      </div>
    </div>
  );
};
